/**
 * Checklist ao vivo das regras do modelo (validacao.ts).
 *
 * Mostra TODAS as regras, e nao so as que falharam: quem escreve precisa ver
 * que "Texto com até 1.024 caracteres" ja passou para confiar que o "Pronto
 * para enviar" do topo quer dizer alguma coisa. As que falharam sobem para o
 * topo, erros antes de avisos.
 *
 * Estado nunca so por cor: cada linha tem glifo diferente (check, x, alerta)
 * e a palavra "erro"/"aviso" para o leitor de tela.
 */

import { Badge, Icon } from '../../../components/ui';
import { contarFalhas, type ResultadoRegra } from '../validacao';

function ordemDe(r: ResultadoRegra): number {
  if (r.ok) {
    return 2;
  }
  return r.severidade === 'erro' ? 0 : 1;
}

export function ChecagemMeta({ regras, passaPelaMeta }: { readonly regras: readonly ResultadoRegra[]; readonly passaPelaMeta: boolean }): JSX.Element {
  const { erros, avisos, ok } = contarFalhas(regras);
  const ordenadas = [...regras].sort((a, b) => ordemDe(a) - ordemDe(b));

  return (
    <section aria-label="Checagem do modelo" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-muted">
          <span className="font-semibold text-ink">
            {ok} de {regras.length}
          </span>{' '}
          regras ok
        </p>
        {erros > 0 ? (
          <Badge tone="critical" dot>
            {erros === 1 ? '1 erro impede salvar' : `${erros} erros impedem salvar`}
          </Badge>
        ) : avisos > 0 ? (
          <Badge tone="warning" dot>
            {avisos === 1 ? 'Pode salvar · 1 aviso' : `Pode salvar · ${avisos} avisos`}
          </Badge>
        ) : (
          <Badge tone="good" dot icon="check">
            {passaPelaMeta ? 'Pronto para enviar à Meta' : 'Pronto para salvar'}
          </Badge>
        )}
      </div>
      <ul className="flex flex-col gap-1.5">
        {ordenadas.map((r) => {
          const estilo = r.ok ? 'border-line bg-surface' : r.severidade === 'erro' ? 'border-status-critical/40 bg-status-critical/5' : 'border-status-warning/50 bg-status-warning/5';
          const icone = r.ok ? 'check' : r.severidade === 'erro' ? 'x' : 'alerta';
          const cor = r.ok ? 'text-status-good' : r.severidade === 'erro' ? 'text-status-critical' : 'text-status-warning';
          return (
            <li key={r.id} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-xs ${estilo}`}>
              <Icon name={icone} size="xs" className={`mt-0.5 shrink-0 ${cor}`} />
              <div className="min-w-0">
                <p className={r.ok ? 'text-ink-muted' : 'font-medium text-ink'}>
                  <span className="sr-only">{r.ok ? 'ok: ' : r.severidade === 'erro' ? 'erro: ' : 'aviso: '}</span>
                  {r.titulo}
                </p>
                {r.ok || r.mensagem === null ? null : <p className="mt-0.5 text-ink-muted">{r.mensagem}</p>}
              </div>
            </li>
          );
        })}
      </ul>
      {passaPelaMeta ? (
        <p className="text-[11px] text-ink-subtle">
          Limites desta prévia: texto até 1.024 caracteres, rodapé até 60, até 2 botões de até 20 caracteres, variáveis {'{nome}'} ou {'{{1}}'} em sequência. A decisão final é da Meta.
        </p>
      ) : null}
    </section>
  );
}
